import { LuxSelectablePill, LuxTypography } from '@gemini/fe-ds-core';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TimerWrapper } from './CalendarPage.styles';
import { usePatientStore } from '@store/patientStore';
import useDeleteHourSlot from '@hooks/useDeleteHourSlot';

const ReservationTimer = ({ initialMinutes = 10, initialSeconds = 0 }) => {
  const { t } = useTranslation();
  const [minutes, setMinutes] = useState(initialMinutes);
  const [seconds, setSeconds] = useState(initialSeconds);
  // store
  const setTimerExpired = usePatientStore(state => state.setTimerExpired);
  const patients = usePatientStore(state => state.patients);

  const reservationIds = useMemo(
    () => patients.map(p => p?.reservationId || 'no-available'), // we need to set a value because is using in the delete service
    [patients],
  );

  // delete reservation
  const { mutate: deleteReservationOneMutateOne } = useDeleteHourSlot({
    reservationId: reservationIds[0],
  });
  const { mutate: deleteReservationOneMutateTwo } = useDeleteHourSlot({
    reservationId: reservationIds[1],
  });
  const { mutate: deleteReservationOneMutateThree } = useDeleteHourSlot({
    reservationId: reservationIds[2],
  });

  const timer = useMemo(() => {
    let timer: string = '1999';
    patients.forEach(p => {
      if (
        p &&
        p.extraInfoScheduleExam &&
        p.extraInfoScheduleExam.timer &&
        new Date(p?.extraInfoScheduleExam.timer) > new Date(timer)
      ) {
        timer = p.extraInfoScheduleExam.timer;
      }
    });
    return timer;
  }, [patients]);
  const timeExpired = minutes <= 0 && seconds <= 0;

  const setTimerValues = (timer: string) => {
    const inputDate = new Date(timer);
    const now = new Date();

    const differenceInMillis = inputDate.getTime() - now.getTime();
    const differenceInSeconds = Math.floor(differenceInMillis / 1000);

    const minutes = Math.floor(differenceInSeconds / 60);
    const seconds = differenceInSeconds % 60;

    setMinutes(minutes);
    setSeconds(seconds);
  };

  const timerText = useMemo(() => {
    return (
      <LuxTypography variant="textMdRegular" component="span">
        {timeExpired ? (
          t('calendarPage.expiredNotification')
        ) : (
          <>
            {t('calendarPage.expiryNotification')}{' '}
            <LuxTypography variant="textMedium">
              {minutes}m:{seconds}s
            </LuxTypography>
          </>
        )}
      </LuxTypography>
    );
  }, [timeExpired, minutes, seconds, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds <= 0) {
        if (minutes <= 0) {
          clearInterval(interval);
        } else {
          setMinutes(minutes - 1);
          setSeconds(59);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds, minutes]);

  useEffect(() => {
    if (timer) {
      setTimerValues(timer);
    }
  }, [timer]);

  useEffect(() => {
    if (timeExpired) {
      reservationIds.forEach((r, i) => {
        if (r !== 'no-available') {
          if (i === 0) deleteReservationOneMutateOne(r);
          if (i === 1) deleteReservationOneMutateTwo(r);
          if (i === 2) deleteReservationOneMutateThree(r);
          setTimerExpired(i);
        }
      });
    }
  }, [timeExpired]);

  return (
    <TimerWrapper>
      <LuxSelectablePill
        selectable={false}
        variant="pill"
        color="warning"
        iconStart="ClockIcon"
        text={timerText}
      ></LuxSelectablePill>
    </TimerWrapper>
  );
};

export default ReservationTimer;
